import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Box,
  Divider,
} from "@mui/material";
import { Delete as DeleteIcon, AdminPanelSettings as AdminIcon } from "@mui/icons-material";
import { db, auth } from "../../firebase-config";
import { collection, getDocs, doc, setDoc, deleteDoc } from "firebase/firestore";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentUser = auth.currentUser;

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersRef = collection(db, "users");
        const usersSnapshot = await getDocs(usersRef);
        const userList = usersSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        const adminsRef = collection(db, "admins");
        const adminsSnapshot = await getDocs(adminsRef);
        const adminList = adminsSnapshot.docs.reduce((acc, doc) => {
          acc[doc.id] = true;
          return acc;
        }, {});

        setUsers(userList);
        setAdmins(adminList);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to load users.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleToggleAdmin = async (userId) => {
    try {
      const adminRef = doc(db, "admins", userId);
      if (admins[userId]) {
        await deleteDoc(adminRef);
        setAdmins((prev) => {
          const updated = { ...prev };
          delete updated[userId];
          return updated;
        });
      } else {
        await setDoc(adminRef, { role: "admin" });
        setAdmins((prev) => ({ ...prev, [userId]: true }));
      }
    } catch (err) {
      console.error("Error updating admin role:", err);
      setError("Failed to update user role.");
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        User Management
      </Typography>

      {users.length === 0 ? (
        <Typography variant="body1">No users found.</Typography>
      ) : (
        <List>
          {users.map((user, index) => (
            <React.Fragment key={user.id}>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary={user.email}
                  secondary={admins[user.id] ? "Admin" : "User"}
                />
                <ListItemSecondaryAction>
                  <Button
                    variant="contained"
                    color={admins[user.id] ? "secondary" : "primary"}
                    startIcon={<AdminIcon />}
                    onClick={() => handleToggleAdmin(user.id)}
                  >
                    {admins[user.id] ? "Remove Admin" : "Make Admin"}
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
              {index !== users.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Container>
  );
};

export default AdminUsers;
